import DownloadIcon from "@mui/icons-material/Download";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  Box,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomIcon } from "../../../../global/components";
import strings from "../../../../global/constants/StringConstants";
import { handleErrorMessage } from "../../../../helpers/methods";
import { selectRole } from "../../../../redux/authSlice";
import { useAppSelector } from "../../../../utils/hooks";
import { boldFont } from "../../../../utils/styles";
import analysisStyles from "../../Analysis.styles";
import { getSingleDashaComponentData } from "../../AnalysisService";
import {
  downloadImageFile,
  downloadPdfFile,
  downloadZipFile,
} from "./MahadashaUtils";

interface CustomProps {
  profileId: string;
  subTabValue: string;
  setIsLoading: Function;
}

const SingleDashaComponent = (props: CustomProps) => {
  const classes = analysisStyles;
  const role = useAppSelector(selectRole);
  const { t, i18n } = useTranslation();
  const [dashaData, setDashaData] = useState([]);
  const [rows, setRows] = useState<any[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const hasTable = [strings.GOOD_TIME, strings.BAD_TIME];

  const planetColorMapping: any = {
    Ketu: "white",
    Venus: "#22cb22",
    Sun: "#22cb22",
    Moon: "#22cb22",
    Mars: "red",
    Rahu: "white",
    Jupiter: "#22cb22",
    Saturn: "red",
    Mercury: "#22cb22",
    // Polish names
    Wenus: "#22cb22", // Venus
    Słońce: "#22cb22", // Sun
    Księżyc: "#22cb22", // Moon
    Jowisz: "#22cb22", // Jupiter
    Merkury: "#22cb22", // Mercury
  };

  const headers = [
    `${t("startDate")}`,
    `${t("endDate")}`,
    `${t("difference")}`,
  ];

  const downloadMenuItems = [
    {
      label: "PDF",
      value: "pdf",
      icon: <PictureAsPdfOutlinedIcon sx={{ mr: 1, color: "#222222" }} />,
    },
    {
      label:
        props.subTabValue === strings.CHANGES_IN_LIFE_SPAN ? "Zip" : "Image",
      value:
        props.subTabValue === strings.CHANGES_IN_LIFE_SPAN ? "zip" : "image",
      icon:
        props.subTabValue === strings.CHANGES_IN_LIFE_SPAN ? (
          <FolderZipOutlinedIcon sx={{ mr: 1, color: "#222222" }} />
        ) : (
          <ImageOutlinedIcon sx={{ mr: 1, color: "#222222" }} />
        ),
    },
  ];

  useEffect(() => {
    getSingleDashaComponentDetails("1");
  }, [props.subTabValue, i18n.language]);

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const getSingleDashaComponentDetails = async (downloadParam: string) => {
    try {
      props.setIsLoading(true);
      const language = i18n.language.toLowerCase();
      const response = await getSingleDashaComponentData(
        props?.profileId!,
        props.subTabValue,
        downloadParam,
        language
      );
      setDashaData(response.dasha);
      hasTable.includes(props.subTabValue) && setRows(response.timeBar);
      props.setIsLoading(false);
    } catch (error: any) {
      props.setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const downloadSingleDashaComponentDetails = async (downloadParam: string) => {
    try {
      props.setIsLoading(true);
      const language = i18n.language.toLowerCase();
      const response = await getSingleDashaComponentData(
        props?.profileId!,
        props.subTabValue,
        downloadParam,
        language
      );
      handleDownload(response, downloadParam);
      props.setIsLoading(false);
      setMenuAnchorEl(null);
    } catch (error: any) {
      props.setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleDownload = (response: any, downloadParam: string) => {
    const baseName = props.subTabValue || "dasha";
    if (downloadParam === "pdf") {
      downloadPdfFile(response, `${baseName}.pdf`);
    } else if (downloadParam === "zip") {
      downloadZipFile(response, `${baseName}.zip`);
    } else if (downloadParam === "image") {
      downloadImageFile(response, `${baseName}.jpg`);
    }
    return;
  };

  const getTable = () => {
    return (
      <Box pt={2} px={1}>
        <TableContainer
          component={Paper}
          sx={{ ...classes.planetTableContainer, borderRadius: "10px" }}
        >
          <Table>
            <TableHead sx={classes.planetTableHead}>
              <TableRow sx={{ borderBottom: "1px solid #ffffff4f" }}>
                {headers.map((header: string) => {
                  return (
                    <TableCell
                      sx={{
                        ...classes.tableHeader,
                        borderRight: "1px solid #ffffff4f",
                      }}
                    >
                      {header}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody sx={classes.planetTableBody}>
              {rows.map((row) => (
                <TableRow key={row.startDate}>
                  <TableCell component="th" sx={classes.tableBody}>
                    {row.startDate}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...classes.tableBody,
                      borderRight: "1px solid #ffffff4f",
                    }}
                  >
                    {row.endDate}
                  </TableCell>
                  <TableCell sx={classes.tableBody}>{row.totalTime}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const getSingleDashaComponent = () => {
    return (
      <Grid container py={2} sx={{ rowGap: "15px" }}>
        {dashaData?.map((data: any) => {
          return (
            <Grid
              item
              xl={12}
              lg={12}
              md={12}
              sm={12}
              xs={12}
              sx={{ ...classes.mahadashaContent, cursor: "auto" }}
            >
              <Stack direction={"column"} pl={2}>
                <Typography
                  sx={{
                    ...classes.planetNameTypo,
                    ...(dashaData.length === 4 && boldFont),
                  }}
                  pb={1.5}
                >
                  {data.planetName
                    .split(/[\s-]+/)
                    .map((part: string, index: number, arr: any) => (
                      <React.Fragment key={index}>
                        <span
                          style={{
                            color: planetColorMapping[part.trim()]
                              ? planetColorMapping[part.trim()]
                              : "white",
                          }}
                        >
                          {part}
                        </span>
                        {index < arr.length - 1 && (
                          <span style={{ color: "white" }}> - </span>
                        )}
                      </React.Fragment>
                    ))}
                </Typography>

                <Stack
                  direction={"row"}
                  spacing={1}
                  pt={1.5}
                  sx={{
                    width: "fit-content",
                    borderTop: "2px solid #ffffff73",
                  }}
                >
                  <Typography sx={classes.dateTypo}>
                    {data.startDate}
                  </Typography>
                  <CustomIcon
                    icon={<HorizontalRuleIcon htmlColor="#9F3AFF" />}
                  />
                  <Typography sx={classes.dateTypo}>{data.endDate}</Typography>
                </Stack>
              </Stack>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const getEmptyComponent = () => {
    return (
      <Stack direction="column" alignItems="center" justifyContent="center">
        {props.subTabValue === strings.GOOD_TIME && (
          <Typography sx={classes.noDataTypo} px={2} pt={1}>
            {t("noGoodTime")}
          </Typography>
        )}
        {props.subTabValue === strings.BAD_TIME && (
          <Typography sx={classes.noDataTypo} px={2} pt={1}>
            {t("noBadTime")}
          </Typography>
        )}
      </Stack>
    );
  };

  const getDownloadButton = () => {
    return (
      <Box
        pt={2}
        display="flex"
        justifyContent="end"
        position="relative"
        px={3}
      >
        <CustomButton
          type={strings.PRIMARY}
          onClick={handleDownloadClick}
          customClasses={{
            maxWidth: "fit-content",
            minWidth: "fit-content",
            "& .MuiButton-startIcon": {
              m: 0,
            },
          }}
          startIcon={<DownloadIcon />}
        />
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1,
              minWidth: 150,
              borderRadius: 2,
              boxShadow: "0px 2px 8px rgba(0,0,0,0.32)",
            },
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {downloadMenuItems.map((item) => (
            <MenuItem
              key={item.value}
              onClick={() => downloadSingleDashaComponentDetails(item.value)}
            >
              {item.icon}
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  };

  return (
    <>
      {role === strings.ADMIN && dashaData.length !== 0 && getDownloadButton()}
      {hasTable.includes(props.subTabValue) && rows.length > 0 && getTable()}
      {getSingleDashaComponent()}
      {rows.length === 0 && dashaData.length === 0 && getEmptyComponent()}
    </>
  );
};

export default SingleDashaComponent;
